import * as PIXI from "pixi.js";
import gsap from "gsap";
import PixiPlugin from "gsap/PixiPlugin";
import i18next from "i18next";
import Backend from "i18next-http-backend";
import Main from "./Main";
import {
  DEBUG,
  DEFAULT_LOCALE,
  EXHIBITION,
  FALLBACK_LOCALE,
} from "./AppConfig";
import { DEFAULT_HISTORY } from "./StaticConfig";
import { loadAssets } from "./loading/AssetLoader";
import GlobalStore from "./services/GlobalStore";
import SignController from "./services/SignController";
import { WebSocketClient } from "./services/WebSocketClient";
import { WebSocketSimulator } from "./services/WebSocketSimulator";
import { LoginMessage, MessageType } from "./models/Messages";
import GameConfig from "./GameConfig";
import { SIMULATION } from "./AppConfig";
import LoadScreen from "./components/LoadScreen";

if (!DEBUG) {
  document.addEventListener("contextmenu", (evt) => {
    evt.preventDefault();
  });
}

(async () => {
  gsap.registerPlugin(PixiPlugin);
  PixiPlugin.registerPIXI(PIXI);

  const params = new URLSearchParams(window.location.search);
  const config = await GameConfig.getInstance().load("config.json");

  // Check for simulation mode via URL parameters
  let forceSimulation = SIMULATION;
  if (params.has("simulation")) {
    forceSimulation = params.get("simulation")?.toLowerCase() === "true";
    console.log("🔧 URL param: simulation =", forceSimulation);
  } else if (params.has("sign_id")) {
    const signId = params.get("sign_id") as string;
    if (signId.toLowerCase() === "simulation") {
      forceSimulation = true;
      console.log(
        "🔧 URL param: sign_id=simulation → enabling simulation mode",
      );
    }
    config.app.sign_id = signId;
  } else {
    forceSimulation = false;
  }

  if (params.has("jackpot_id")) {
    let jackpotIdOverride = Number.parseInt(params.get("jackpot_id") ?? "");
    config.app.jackpot_id = jackpotIdOverride;
  }

  // Load all display settings from config.json with defaults
  GlobalStore.generalSettings.showMachineIdOnHitScreen =
    config.display?.showMachineIdOnHitScreen ?? true;
  GlobalStore.generalSettings.showDateOnHitScreen =
    config.display?.showDateOnHitScreen ?? true;
  GlobalStore.generalSettings.showTimeOnHitScreen =
    config.display?.showTimeOnHitScreen ?? true;
  GlobalStore.generalSettings.showMachineIdInHistory =
    config.display?.showMachineIdInHistory ?? true;
  // GlobalStore.generalSettings.showJackpotNameInHistory =
  //   config.display?.showJackpotNameInHistory ?? true;
  GlobalStore.generalSettings.showTimeInHistory =
    config.display?.showTimeInHistory ?? DEFAULT_HISTORY.showTimeInHistory;
  GlobalStore.generalSettings.currencySymbol =
    config.display?.currencySymbol ?? "RD$";

  // Load decimal display setting
  GlobalStore.generalSettings.showDecimals =
    config.display?.showDecimals ?? true;

  // Load timing configuration with defaults
  GlobalStore.timings = {
    jackpotHitScreenDurationSeconds:
      config.timings?.jackpotHitScreenDurationSeconds ?? 45,
    currentJackpotScreenDurationSeconds:
      config.timings?.currentJackpotScreenDurationSeconds ?? 30,
    historyScreenDurationSeconds:
      config.timings?.historyScreenDurationSeconds ?? 10,
  };

  // if (params.has("jackpot_id")) {
  //   const _jpid = Number(params.get("jackpot_id"));
  //   GlobalStore.addActiveJackpotId(_jpid);
  //   config.current_jackpot.jackpots[0].id = _jpid;
  // }
  // for (const jp of config.current_jackpot.jackpots) {
  //   GlobalStore.addActiveJackpotId(jp.id);
  // }

  GlobalStore.setLocale({
    date: config.localization.locale,
    currency: config.localization.locale,
  });

  await i18next.use(Backend).init({
    lng: config.localization.language ?? DEFAULT_LOCALE.toLocaleLowerCase(),
    fallbackLng: FALLBACK_LOCALE.toLocaleLowerCase(),
    debug: DEBUG,
    backend: {
      loadPath: config.localization.languagePath ?? "./language/{{lng}}.json",
    },
  });

  // Create a custom currency formatter that respects the currencySymbol override
  const createCurrencyFormatter = () => {
    const symbol = GlobalStore.generalSettings.currencySymbol;
    const showDecimals = GlobalStore.generalSettings.showDecimals;

    return (value: number) => {
      // Format the number with proper decimals
      const numberFormat = new Intl.NumberFormat(config.localization.locale, {
        minimumFractionDigits: showDecimals ? 2 : 0,
        maximumFractionDigits: showDecimals ? 2 : 0,
      });

      const formattedNumber = numberFormat.format(value);

      // Prepend the custom currency symbol if provided
      if (symbol) {
        return `${symbol}  ${formattedNumber}`;
      }
      return formattedNumber;
    };
  };

  GlobalStore.setCurrencyFormatter(createCurrencyFormatter());

  const app = new PIXI.Application();

  await app.init({
    width: config.app.screenWidth,
    height: config.app.screenHeight,
    antialias: true,
    background: 0x212529,
  });
  app.ticker.maxFPS = 30;

  const wrap = document.createElement("div");
  wrap.classList.add("canvas-wrap");
  wrap.appendChild(app.canvas);
  document.body.appendChild(wrap);

  GlobalStore.setSignId(config.app.sign_id);
  if (config.app.jackpot_id) {
    GlobalStore.setJackpotIdOverride(config.app.jackpot_id);
  }

  const layers = GlobalStore.getLayers();
  (Object.keys(layers) as Array<keyof typeof layers>).forEach((key) => {
    app.stage.addChild(layers[key]);
  });

  // const gfx = new PIXI.Graphics();
  // gfx.zIndex = 1999999;
  // gfx
  //   .rect(0, 0, config.app.screenWidth, config.app.screenHeight)
  //   .fill("0xfafafa55")
  //   .rect(0, 0, config.app.screenWidth / 2, config.app.screenHeight)
  //   .fill("0xfa00fa55");
  // app.stage.addChild(gfx);

  // let loader: Loader | undefined;

  // console.time("Loading");

  let loader: LoadScreen | null = null;

  try {
    await loadAssets({
      onLoadingAssetsLoaded: () => {
        // Display the loading scene
        // after loading scene assets are loaded
        // loader = new Loader(100, 100);
        // app.stage.addChild(loader);
        // GlobalStore.layers.loader.attach(loader);
        loader = new LoadScreen();
        app.stage.addChild(loader);
        GlobalStore.getLayers().content.attach(loader);
      },
      onProgress: (progress) => {
        if (loader) {
          loader.onData(progress);
        }
      },
    });

    // console.timeEnd("Loading");

    if (loader !== null) {
      GlobalStore.getLayers().content.detach(loader);
      app.stage.removeChild(loader);
    }
  } catch (error) {
    console.error("Failed to load assets:", error);
    if (loader) {
      (loader as LoadScreen).onError(
        error instanceof Error ? error : new Error(String(error)),
      );
    }
    // Don't proceed with app initialization if asset loading failed
    return;
  }

  // TODO: Instantiate SoundManager
  //

  const wsClient = new WebSocketClient({
    json: true,
    url: `${config.app.websocket_address}?sign_id=${config.app.sign_id}`,
  });

  // Initialize SignController first (it sets up the message handler)
  const controller = SignController.init(wsClient, app, config);

  // Use simulator for exhibition builds or URL override
  console.log(
    "DEBUG: SIMULATION flag =",
    SIMULATION,
    "EXHIBITION flag =",
    EXHIBITION,
    "Force simulation =",
    forceSimulation,
  );
  if (SIMULATION || forceSimulation) {
    console.log("✅ Simulation mode ACTIVE - using WebSocket simulator");
    try {
      console.log("🏗️  Creating WebSocketSimulator instance...");
      const simulator = new WebSocketSimulator(wsClient);

      console.log("🚀 Initializing simulator...");
      // Initialize and start the simulator
      await simulator.initialize();

      console.log("🎮 Starting simulator...");
      simulator.start();
      console.log("✅ Simulator started successfully!");
    } catch (error) {
      console.error("❌ Failed to start simulator:", error);
    }
  } else {
    // Normal WebSocket connection for production
    wsClient.onOpen = () => {
      const loginMsg: LoginMessage = {
        msgType: MessageType.LOGIN,
        signId: GlobalStore.getSignId(),
      };
      wsClient.send(loginMsg);
    };
    wsClient.connect();
  }

  const main = new Main({ app: app, controller: controller, config: config });
})();
